<script setup>
import { ref, watch, computed, onMounted, nextTick, onBeforeMount } from 'vue';
import { useStore } from 'vuex';
import Textarea from '../../../../../components-next/textarea/TextArea.vue';
import Button from '../../../../../components-next/button/Button.vue';
import ValidationFeedback from '../shared/ValidationFeedback.vue';

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  teamLabel: {
    type: String,
    default: 'Time',
  },
  teamPlaceholder: {
    type: String,
    default: 'Selecione um time',
  },
  descriptionLabel: {
    type: String,
    default: 'Contexto de Atribuição do Time',
  },
  descriptionPlaceholder: {
    type: String,
    default: 'Digite quando o agente deve atribuir a conversa a este time',
  },
});

const emit = defineEmits(['update', 'validation']);

const store = useStore();
const localItems = ref([...props.items]);
// Array para armazenar quais campos foram tocados
const touchedItems = ref([]);
// Array para controlar estado dos dropdowns
const dropdownStates = ref([]);

// Buscar todos os times disponíveis
onBeforeMount(() => {
  store.dispatch('teams/get');
});

// Computed para pegar os times do store
const availableTeams = computed(() => {
  return store.getters['teams/getTeams'] || [];
});

// Computed para times já selecionados (para evitar duplicatas)
const selectedTeamIds = computed(() => {
  return localItems.value.map(item => item.teamId).filter(Boolean);
});

// Computed para opções do select (excluindo times já selecionados)
const teamOptions = computed(() => {
  return availableTeams.value
    .filter(team => !selectedTeamIds.value.includes(team.id))
    .map(team => ({
      value: team.id,
      label: team.name,
      description: team.description
    }));
});

// Computed para verificar se todos os itens são válidos
const areAllItemsValid = computed(() => {
  // Se não houver itens, é válido (campos são opcionais)
  if (localItems.value.length === 0) return true;
  
  // Se houver itens, todos devem ter time e descrição preenchidos
  return localItems.value.every(item => {
    return item.teamId && item.description && item.description.trim();
  });
});

// Emite o estado de validação sempre que muda
watch(areAllItemsValid, (newValue) => {
  emit('validation', newValue);
});

// Emite o estado inicial na montagem
onMounted(() => {
  // Inicializa localItems com teamError e descriptionError
  localItems.value.forEach(item => {
    if (!item.hasOwnProperty('teamError')) item.teamError = '';
    if (!item.hasOwnProperty('descriptionError')) item.descriptionError = '';
  });
  
  // Inicializa estados dos dropdowns
  dropdownStates.value = new Array(localItems.value.length).fill(false);
  
  // Atualiza o estado de validação
  emit('validation', areAllItemsValid.value);
});

const removeItem = async (index) => {
  localItems.value.splice(index, 1);
  touchedItems.value.splice(index, 1);
  dropdownStates.value.splice(index, 1);
  updateItems();
  
  // Aguarda o próximo ciclo de renderização antes de validar
  await nextTick();
  
  // Força a revalidação após remoção
  emit('validation', areAllItemsValid.value);
};

const handleBlur = (index, field) => {
  // Marca o campo como tocado
  if (!touchedItems.value[index]) {
    touchedItems.value[index] = { teamId: false, description: false };
  }
  
  touchedItems.value[index][field] = true;
  
  // Valida o campo
  validateField(index, field);
};

const validateField = (index, field) => {
  const item = localItems.value[index];
  
  // Só valida se o campo já foi tocado
  if (!touchedItems.value[index] || !touchedItems.value[index][field]) {
    return;
  }
  
  if (field === 'teamId' && !item.teamId) {
    item.teamError = 'Seleção de time é obrigatória';
  } else {
    item.teamError = '';
  }
  
  if (field === 'description' && !item.description?.trim()) {
    item.descriptionError = 'Contexto de Atribuição é obrigatório';
  } else {
    item.descriptionError = '';
  }
  
  // Emite atualização após validação
  updateItems();
};

const updateItems = () => {
  emit('update', [...localItems.value]);
};

// Função para obter o nome do time pelo ID
const getTeamName = (teamId) => {
  const team = availableTeams.value.find(t => t.id === teamId);
  return team ? team.name : '';
};

// Função para obter a descrição padrão do time pelo ID
const getTeamDefaultDescription = (teamId) => {
  const team = availableTeams.value.find(t => t.id === teamId);
  return team ? team.description : '';
};

// Watch para sincronizar com as props
watch(() => props.items, (newItems) => {
  if (JSON.stringify(newItems) !== JSON.stringify(localItems.value)) {
    localItems.value = [...newItems];
    
    // Inicializa as flags de erro para cada item
    localItems.value.forEach(item => {
      if (!item.hasOwnProperty('teamError')) item.teamError = '';
      if (!item.hasOwnProperty('descriptionError')) item.descriptionError = '';
    });
    
    // Reset touched status for new items
    touchedItems.value = new Array(newItems.length).fill().map(() => ({ teamId: false, description: false }));
    
    // Reset dropdown states for new items
    dropdownStates.value = new Array(newItems.length).fill(false);
    
    // Emite o estado de validação ao receber novos itens
    emit('validation', areAllItemsValid.value);
  }
}, { deep: true });

// Handler para mudança de time
const handleTeamChange = (index, teamId) => {
  localItems.value[index].teamId = teamId;
  
  // Fecha o dropdown
  dropdownStates.value[index] = false;
  
  // Se o campo foi tocado, valida
  if (touchedItems.value[index]?.teamId) {
    validateField(index, 'teamId');
  }
  
  updateItems();
};

// Controle do dropdown
const toggleDropdown = (index) => {
  // Fecha todos os outros dropdowns
  dropdownStates.value.forEach((_, i) => {
    if (i !== index) {
      dropdownStates.value[i] = false;
    }
  });
  
  // Toggle do dropdown atual
  dropdownStates.value[index] = !dropdownStates.value[index];
};

const closeDropdown = (index) => {
  dropdownStates.value[index] = false;
};

const closeAllDropdowns = () => {
  dropdownStates.value.fill(false);
};

// Função para obter o texto exibido no botão do dropdown
const getSelectedTeamLabel = (teamId) => {
  if (!teamId) return props.teamPlaceholder;
  const team = availableTeams.value.find(t => t.id === teamId);
  return team ? team.name : props.teamPlaceholder;
};
</script>

<template>
  <div class="space-y-4">
    <TransitionGroup name="list-fade" tag="div">
      <div 
        v-for="(item, index) in localItems" 
        :key="`teams-data-field-${index}`" 
        class="relative bg-n-slate-2 dark:bg-n-slate-12/5 p-4 rounded-lg border border-n-slate-5 dark:border-n-slate-10/20 mt-7 team-field-item dynamic-field-container"
      >
        <!-- Cabeçalho -->
        <div class="flex items-center justify-between mb-3">
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ localItems.length > 1 ? `Time ${localItems.length - index}` : 'Time' }}
          </h4>
          <Button
            icon="i-lucide-trash-2"
            variant="faded"
            color="ruby"
            size="xs"
            class="remove-button-rotate"
            @click="removeItem(index)"
            v-tooltip.top="'Remover time'"
          />
        </div>
        
        <!-- Grid de campos -->
        <div class="space-y-3">
          <!-- Seleção de Time -->
          <div>
            <label class="text-xs text-n-slate-11 mb-1 block">{{ teamLabel }} *</label>
            <div class="relative w-full custom-dropdown" v-on-clickaway="() => closeDropdown(index)">
              <!-- Botão do dropdown -->
              <button 
                @click="toggleDropdown(index)" 
                @blur="handleBlur(index, 'teamId')"
                type="button"
                :class="[
                  'flex items-center justify-between px-3 h-[38px] text-sm border rounded-lg transition-colors cursor-pointer w-full',
                  'bg-n-slate-1 border-n-slate-6 text-n-slate-12 hover:border-primary-600 focus:border-primary-600 focus:ring-2 focus:ring-primary-600/20 focus:outline-none',
                  'dark:bg-n-slate-1 dark:border-n-slate-6 dark:text-n-slate-12 dark:hover:border-primary-600 dark:focus:border-primary-600 dark:focus:ring-primary-600/20',
                  { 'border-n-ruby-8': item.teamError }
                ]"
              >
                <span :class="{ 'text-n-slate-9': !item.teamId }">{{ getSelectedTeamLabel(item.teamId) }}</span>
                <span 
                  class="i-lucide-chevron-down ml-2 h-4 w-4 text-n-slate-10 transition-transform"
                  :class="{ 'rotate-180': dropdownStates[index] }"
                ></span>
              </button>

              <!-- Lista de opções -->
              <div
                v-if="dropdownStates[index]"
                class="absolute top-full mt-1 left-0 w-full bg-n-slate-1 border border-n-slate-6 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                style="z-index: 50;"
              >
                <button 
                  v-for="option in teamOptions" 
                  :key="option.value" 
                  @click="handleTeamChange(index, option.value)" 
                  type="button"
                  class="w-full px-3 py-2 text-sm text-left hover:bg-n-slate-3 transition-colors first:rounded-t-lg last:rounded-b-lg flex items-center gap-2"
                  :class="{ 'bg-n-slate-3': item.teamId === option.value }"
                >
                  <span class="i-lucide-users w-4 h-4 text-n-slate-10"></span>
                  <span class="truncate">{{ option.label }}</span>
                </button>
                
                <div v-if="teamOptions.length === 0" class="px-3 py-2 text-sm text-n-slate-9 text-center">
                  Nenhum time disponível
                </div>
              </div>
            </div>
            
            <!-- Exibir erro de validação -->
            <div v-if="item.teamError" class="mt-1 text-xs text-n-ruby-11">
              {{ item.teamError }}
            </div>
          </div>
          
          <!-- Descrição Padrão do Time (somente leitura) -->
          <div v-if="item.teamId">
            <label class="text-xs text-n-slate-11 mb-1 flex items-center gap-1">
              Descrição do Time
              <span 
                class="i-lucide-info w-3 h-3 text-n-slate-9"
                v-tooltip.top="`A descrição do time vem das configurações do sistema e não pode ser editada aqui. Para editar a descrição, acesse Configurações → Times.`"
              ></span>
            </label>
            <div class="min-h-[2.5rem] px-3 py-2 text-sm bg-n-slate-1 border border-n-slate-5 rounded-lg text-n-slate-11">
              <span v-if="getTeamDefaultDescription(item.teamId)" class="whitespace-pre-wrap">{{ getTeamDefaultDescription(item.teamId) }}</span>
              <span v-else class="italic text-n-slate-9">Descrição não foi definida para este time.</span>
            </div>
          </div>

          <!-- Descrição Personalizada - Usando Textarea -->
          <div>
            <div class="flex items-center justify-between mb-1">
              <label class="text-xs text-n-slate-11">{{ descriptionLabel }} *</label>
              <span v-if="item.description" class="text-xs text-n-slate-10">{{ item.description.length }}</span>
            </div>
            <Textarea
              v-model="item.description"
              :placeholder="descriptionPlaceholder"
              :error="item.descriptionError"
              :rows="2"
              :autoHeight="true"
              :resize="true"
              :minHeight="'2.5rem'"
              :maxHeight="'8rem'"
              @blur="handleBlur(index, 'description')"
              @input="() => { if (touchedItems[index]?.description) validateField(index, 'description'); }"
            />
          </div>
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<style lang="scss" scoped>
// Importar animações compartilhadas
@import '../shared/DynamicFieldAnimations.scss';

// Estilos específicos para times
.team-field-item {
  transition: transform 0.15s ease-out, box-shadow 0.15s ease-out;
  position: relative;
  z-index: 1;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    z-index: 100; // Z-index alto para o container em hover
  }

  // Quando o dropdown está aberto, aumenta ainda mais o z-index
  &:has(.custom-dropdown div[style*="z-index: 50"]) {
    z-index: 200 !important;
  }
}

// Garantir que o dropdown sempre fique acima
.custom-dropdown {
  position: relative;

  > div[style*="z-index: 50"] {
    z-index: 50 !important;
    position: absolute !important;
  }
}

// Animação do dropdown
.team-dropdown {
  transition: all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  &.dropdown-open {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border-color: var(--primary-600);
  }
}

// Animação de seleção de time
.team-option {
  transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  
  &:hover {
    transform: translateX(4px);
  }
}

// Badge do time com animação
.team-badge {
  transition: all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
}

// Animação ao abrir/fechar dropdown
.dropdown-list-enter-active,
.dropdown-list-leave-active {
  transition: all 0.2s ease;
}

.dropdown-list-enter-from {
  opacity: 0;
  transform: translateY(-8px);
}

.dropdown-list-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>